import { useEffect, useRef, useState, type ReactNode } from "react";
import { TermsDocument } from "@/components/TermsDocument";

/** A document at sign-up, the Terms of Service unless another is given: the
 * person scrolls to the end, then "I agree" turns on. Cancel closes without
 * accepting. */
export function TermsModal({
  onAgree,
  onCancel,
  title = "Terms of Service",
  children = <TermsDocument />,
}: {
  onAgree: () => void;
  onCancel: () => void;
  title?: string;
  children?: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [readToEnd, setReadToEnd] = useState(false);

  function check() {
    const el = box.current;
    if (el && el.scrollHeight > 0 && el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setReadToEnd(true);
  }

  // Text short enough to fit without scrolling counts as read.
  useEffect(check, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl border border-line bg-surface shadow-2xl">
        <div className="border-b border-line-subtle px-5 py-4">
          <h2 className="text-base font-semibold text-fg">{title}</h2>
          <p className="text-xs text-fg-muted">Please read to the end to continue.</p>
        </div>
        <div ref={box} onScroll={check} data-testid="terms-scroll" className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-line-subtle px-5 py-3">
          <span className="text-xs text-fg-faint">{readToEnd ? "You've reached the end." : "Scroll to the end to agree."}</span>
          <div className="flex gap-2">
            <button type="button" onClick={onCancel} className="whitespace-nowrap rounded-md border border-line px-3 py-1.5 text-sm text-fg-muted hover:text-fg">
              Cancel
            </button>
            <button
              type="button"
              onClick={onAgree}
              disabled={!readToEnd}
              className="whitespace-nowrap rounded-md bg-electric-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-electric-blue/90 disabled:opacity-40"
            >
              I agree
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
